import { keepPreviousData } from '@tanstack/react-query'
import ky, { type AfterResponseState } from 'ky'
import { isApiFailure } from '@agion/ui/api-failure'

/** A 408 or 429: the server asks the caller to come back later. */
function asksToComeBack(status: number): boolean {
  return status === 408 || status === 429
}

/**
 * The server answered, and asking again gets the same answer: any 4xx but 408
 * and 429 (agion/Agion#2727 for 403, agion/Agion#2777 for the rest). No
 * response, a 408, a 429 and a 5xx stay worth another try.
 */
function isSettledAnswer(error: Error): boolean {
  return isApiFailure(error) && error.status >= 400 && error.status < 500 && !asksToComeBack(error.status)
}

/** Retry a failed read once, unless the server's answer is settled (see isSettledAnswer). */
export function retryUnlessRefused(failureCount: number, error: Error): boolean {
  if (isSettledAnswer(error)) return false
  return failureCount < 1
}

/**
 * The HTTP adapters' ky `shouldRetry`: a settled answer (any 4xx but 408 and
 * 429) is never asked again inside one call. The adapters throw their own
 * failure from afterResponse, which ky does not read as an HTTPError, so its
 * status-code list never applies. Every other failure returns undefined and
 * keeps ky's default decision; this never returns true, which would skip ky's
 * method check.
 */
export function adapterRetryUnlessRefused({ error }: { error: Error }): false | undefined {
  return isSettledAnswer(error) ? false : undefined
}

/** The longest wait a Retry-After header can ask of an adapter. */
export const RETRY_AFTER_MAX_MS = 10_000

/**
 * The wait a response's Retry-After asks for, in ms: delay-seconds or an
 * HTTP-date (a date already passed is no wait), clamped to RETRY_AFTER_MAX_MS.
 * Undefined when the header is missing or unreadable, so ky keeps its backoff.
 */
export function retryAfterDelayMs(response: Response, now: number = Date.now()): number | undefined {
  const value = response.headers.get('Retry-After')?.trim()
  if (!value) return undefined
  let wait: number
  if (/^\d+$/.test(value)) {
    wait = Number(value) * 1000
  } else if (/^[A-Za-z]/.test(value)) {
    const at = Date.parse(value)
    if (Number.isNaN(at)) return undefined
    wait = at - now
  } else {
    return undefined
  }
  return Math.min(Math.max(wait, 0), RETRY_AFTER_MAX_MS)
}

/**
 * Ky 2 treats failures thrown from afterResponse as fatal. Retry transient
 * GET answers explicitly, respecting the attempt limit and the server's own
 * retry refusal. A readable Retry-After on 408/429 keeps its bounded wait;
 * other answers keep Ky's configured backoff. The last answer reaches the
 * caller as the adapter's typed failure rather than a ForceRetryError.
 */
export function retryAfterAnswer(
  { request, options, response, retryCount }: AfterResponseState,
  errorData?: unknown,
): ReturnType<typeof ky.retry> | undefined {
  // Ky 2 treats errors thrown from hooks as fatal. Request the bounded
  // retry explicitly before throwing our typed failure on the final attempt.
  if (typeof errorData === 'object' && errorData !== null && 'retryable' in errorData && errorData.retryable === false) return undefined
  if (!asksToComeBack(response.status) && (response.status < 500 || response.status >= 600)) return undefined
  if (request.method.toUpperCase() !== 'GET') return undefined
  if (retryCount >= (options.retry?.limit ?? 0)) return undefined
  const delay = asksToComeBack(response.status) ? retryAfterDelayMs(response) : undefined
  return ky.retry(delay === undefined ? {} : { delay })
}

export const QUERY_STALE_TIME = {
  realtime: 10 * 1000,
  operational: 60 * 1000,
  catalog: 5 * 60 * 1000,
  static: 60 * 60 * 1000,
} as const

export const QUERY_GC_TIME = {
  realtime: 2 * 60 * 1000,
  operational: 10 * 60 * 1000,
  catalog: 30 * 60 * 1000,
  static: 24 * 60 * 60 * 1000,
} as const

export const queryPolicies = {
  // Privileged evidence must re-authorize on entry and leave no inactive cache.
  privilegedEvidence: {
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: false,
  },

  realtime: {
    staleTime: QUERY_STALE_TIME.realtime,
    gcTime: QUERY_GC_TIME.realtime,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: retryUnlessRefused,
  },

  operational: {
    staleTime: QUERY_STALE_TIME.operational,
    gcTime: QUERY_GC_TIME.operational,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: retryUnlessRefused,
  },

  catalog: {
    staleTime: QUERY_STALE_TIME.catalog,
    gcTime: QUERY_GC_TIME.catalog,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: retryUnlessRefused,
  },

  static: {
    staleTime: QUERY_STALE_TIME.static,
    gcTime: QUERY_GC_TIME.static,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: retryUnlessRefused,
  },

  pagedCatalog: {
    staleTime: QUERY_STALE_TIME.catalog,
    gcTime: QUERY_GC_TIME.catalog,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: retryUnlessRefused,
    placeholderData: keepPreviousData,
  },

  /**
   * Polling — query is driven by `refetchInterval` rather than cache
   * invalidation. staleTime: 0 + refetchOnMount: 'always' is the load-
   * bearing pattern: every observer mount triggers an immediate fetch
   * (no stale flicker) and the interval handles the "still pending?"
   * checks. Used for bootstrap-state badges and short-lived progress
   * surfaces; pair with `refetchInterval` and consider `retry: false`
   * since the next poll covers retries.
   */
  polling: {
    staleTime: 0,
    gcTime: QUERY_GC_TIME.realtime,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: retryUnlessRefused,
  },
} as const
